import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Users, Clock, ChevronRight, Search, X, Swords, Trophy, Flame, Filter } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '/api';

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  } catch { return dateStr; }
}

function statusLabel(s) {
  return {
    registration: 'Inscriptions', seeding: 'Seeding',
    in_progress: 'En cours', 'in-progress': 'En cours', finished: 'Terminé'
  }[s] || s;
}

function formatLabel(f) {
  return {
    single_elimination: 'Élim. simple',
    'single-elimination': 'Élim. simple',
    double_elimination: 'Élim. double',
    'double-elimination': 'Élim. double',
    round_robin: 'Round Robin',
    swiss: 'Suisse',
  }[f] || f;
}

const STATUS_TABS = [
  { id: 'all',      label: 'Tous',      icon: <Swords size={14} /> },
  { id: 'upcoming', label: 'À venir',   icon: <Flame size={14} />,   match: t => t.status === 'registration' || t.status === 'seeding' },
  { id: 'live',     label: 'En cours',  icon: <Trophy size={14} />,  match: t => t.status === 'in_progress' || t.status === 'in-progress' },
  { id: 'finished', label: 'Terminés',  icon: <Clock size={14} />,   match: t => t.status === 'finished' },
];

export default function TournamentsPage() {
  const [allTournaments, setAllTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch]             = useState('');
  const [formatFilter, setFormatFilter] = useState('all');
  const [teamsFilter, setTeamsFilter]   = useState('all');
  const [sortBy, setSortBy]             = useState('date_asc');
  const [showFilters, setShowFilters]   = useState(false);

  useEffect(() => {
    fetch(`${API}/tournaments`)
      .then(r => r.json())
      .then(data => { setAllTournaments(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const formats = useMemo(() => {
    const seen = new Set();
    allTournaments.forEach(t => { if (t.format) seen.add(t.format); });
    return [...seen];
  }, [allTournaments]);

  const counts = useMemo(() => {
    const obj = {};
    STATUS_TABS.forEach(tab => {
      obj[tab.id] = tab.match ? allTournaments.filter(tab.match).length : allTournaments.length;
    });
    return obj;
  }, [allTournaments]);

  const filtered = useMemo(() => {
    const tab = STATUS_TABS.find(t => t.id === statusFilter);
    let result = tab?.match ? allTournaments.filter(tab.match) : [...allTournaments];

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(t =>
        t.name?.toLowerCase().includes(q) ||
        (t.region || '').toLowerCase().includes(q) ||
        (t.format || '').toLowerCase().includes(q)
      );
    }

    if (formatFilter !== 'all') result = result.filter(t => t.format === formatFilter);
    if (teamsFilter === 'open')  result = result.filter(t => (t.teams?.length || 0) < (t.maxTeams || 16));
    if (teamsFilter === 'full')  result = result.filter(t => (t.teams?.length || 0) >= (t.maxTeams || 16));

    result.sort((a, b) => {
      if (sortBy === 'date_asc')   return new Date(a.startDate) - new Date(b.startDate);
      if (sortBy === 'date_desc')  return new Date(b.startDate) - new Date(a.startDate);
      if (sortBy === 'teams_desc') return (b.teams?.length || 0) - (a.teams?.length || 0);
      if (sortBy === 'teams_asc')  return (a.teams?.length || 0) - (b.teams?.length || 0);
      return 0;
    });

    return result;
  }, [allTournaments, statusFilter, search, formatFilter, teamsFilter, sortBy]);

  const hasActiveFilters = statusFilter !== 'all' || search || formatFilter !== 'all' || teamsFilter !== 'all' || sortBy !== 'date_asc';

  const clearFilters = () => {
    setStatusFilter('all'); setSearch('');
    setFormatFilter('all'); setTeamsFilter('all'); setSortBy('date_asc');
  };

  // Tournoi mis en avant (le prochain avec inscriptions ouvertes)
  const featuredTournament = useMemo(() => {
    return allTournaments.find(t => t.status === 'registration' && t.logo);
  }, [allTournaments]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="tpl-page">

      {/* ── Hero Arène ── */}
      <div className="tpl-hero">
        <div className="tpl-hero-bg" />
        <div className="tpl-hero-orbs">
          <div className="tpl-hero-orb tpl-hero-orb--1" />
          <div className="tpl-hero-orb tpl-hero-orb--2" />
          <div className="tpl-hero-orb tpl-hero-orb--3" />
        </div>
        <div className="tpl-hero-content">
          <motion.div
            className="tpl-hero-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="tpl-hero-icon-row">
              <Swords size={36} className="tpl-hero-swords" />
            </div>
            <h1>L'Arène</h1>
            <p className="tpl-hero-sub">
              Affrontez les meilleures équipes. Rejoignez un tournoi et prouvez votre valeur.
            </p>
            <div className="tpl-hero-stats">
              <div className="tpl-hero-stat">
                <span className="tpl-hero-stat-num">{allTournaments.length}</span>
                <span className="tpl-hero-stat-label">Tournois</span>
              </div>
              <div className="tpl-hero-stat-divider" />
              <div className="tpl-hero-stat">
                <span className="tpl-hero-stat-num">{counts.upcoming || 0}</span>
                <span className="tpl-hero-stat-label">À venir</span>
              </div>
              <div className="tpl-hero-stat-divider" />
              <div className="tpl-hero-stat">
                <span className="tpl-hero-stat-num">{counts.live || 0}</span>
                <span className="tpl-hero-stat-label">En cours</span>
              </div>
            </div>
          </motion.div>

          {/* Featured tournament card in hero */}
          {featuredTournament && (
            <motion.div
              className="tpl-featured"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25 }}
            >
              <Link to={`/tournament/${featuredTournament._id}`} className="tpl-featured-link">
                {featuredTournament.logo && (
                  <div className="tpl-featured-img" style={{ backgroundImage: `url(${featuredTournament.logo})` }} />
                )}
                <div className="tpl-featured-overlay" />
                <div className="tpl-featured-info">
                  <span className="tpl-featured-label"><Flame size={12} /> À la une</span>
                  <span className="tpl-featured-name">{featuredTournament.name}</span>
                  <span className="tpl-featured-meta">
                    <MapPin size={11} /> {featuredTournament.region}
                    <Users size={11} /> {featuredTournament.teams?.length || 0}/{featuredTournament.maxTeams}
                  </span>
                </div>
              </Link>
            </motion.div>
          )}
        </div>
      </div>

      <div className="lp-container">

        {/* ── Toolbar: search + tabs + filters ── */}
        <div className="tpl-toolbar">
          <div className="tpl-toolbar-top">
            <div className="tpl-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Rechercher un tournoi..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="tpl-search-clear" onClick={() => setSearch('')}>
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              className={`tpl-filter-toggle${showFilters ? ' active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={15} />
              Filtres
              {hasActiveFilters && <span className="tpl-filter-dot" />}
            </button>
          </div>

          {/* Status tabs */}
          <div className="tpl-status-tabs">
            {STATUS_TABS.map(tab => (
              <button
                key={tab.id}
                className={`tpl-tab${statusFilter === tab.id ? ' tpl-tab--active' : ''}`}
                onClick={() => setStatusFilter(tab.id)}
              >
                {tab.icon}
                {tab.label}
                {!loading && (
                  <span className="tpl-tab-count">{counts[tab.id]}</span>
                )}
              </button>
            ))}
          </div>

          {/* Filtres avancés (dépliables) */}
          {showFilters && (
            <motion.div
              className="tpl-filter-controls"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              {formats.length > 0 && (
                <select className="tpl-select" value={formatFilter} onChange={e => setFormatFilter(e.target.value)}>
                  <option value="all">Type : Tous</option>
                  {formats.map(f => <option key={f} value={f}>{formatLabel(f)}</option>)}
                </select>
              )}
              <select className="tpl-select" value={teamsFilter} onChange={e => setTeamsFilter(e.target.value)}>
                <option value="all">Places : Toutes</option>
                <option value="open">Places disponibles</option>
                <option value="full">Complet</option>
              </select>
              <select className="tpl-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="date_asc">Date (plus proche)</option>
                <option value="date_desc">Date (plus tard)</option>
                <option value="teams_desc">Equipes (plus)</option>
                <option value="teams_asc">Equipes (moins)</option>
              </select>
              {hasActiveFilters && (
                <button className="tpl-clear-btn" onClick={clearFilters}>
                  <X size={13} /> Tout effacer
                </button>
              )}
            </motion.div>
          )}
        </div>

        {/* ── Résultats ── */}
        <div className="tpl-results-info">
          <span>{loading ? 'Chargement...' : `${filtered.length} tournoi${filtered.length !== 1 ? 's' : ''}`}</span>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="tpl-list">
            {[...Array(4)].map((_, i) => <div key={i} className="lp-skeleton tpl-skeleton-row" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="tpl-empty">
            <Swords size={48} />
            <p>Aucun tournoi ne correspond à vos critères.</p>
            {hasActiveFilters && (
              <button className="tpl-clear-btn" onClick={clearFilters}>
                <X size={13} /> Réinitialiser les filtres
              </button>
            )}
          </div>
        ) : (
          <div className="tpl-list">
            {filtered.map((t, i) => <TournamentCard key={t._id} tournament={t} index={i} />)}
          </div>
        )}

      </div>
    </motion.div>
  );
}

/* Carte tournoi — format horizontal 1 colonne */
function TournamentCard({ tournament: t, index }) {
  const teamsCount = t.teams?.length || 0;
  const maxTeams = t.maxTeams || 16;
  const bgUrl = t.logo || t.image || null;
  const isOpen = t.status === 'registration' && teamsCount < maxTeams;
  const statusClass = (t.status || '').replace(/_/g, '-');
  const fillPercent = Math.round((teamsCount / maxTeams) * 100);
  const spotsLeft = maxTeams - teamsCount;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.3), type: 'spring', stiffness: 280, damping: 24 }}
    >
      <Link to={`/tournament/${t._id}`} className="tpl-row-card">
        {/* Bannière gauche */}
        <div className="tpl-row-banner">
          {bgUrl ? (
            <img src={bgUrl} alt={t.name} className="tpl-row-banner-img" />
          ) : (
            <div className="tpl-row-banner-fallback">
              <Trophy size={28} />
            </div>
          )}
          <span className={`tpl-row-status tpl-row-status--${statusClass}`}>
            {statusLabel(t.status)}
          </span>
        </div>

        {/* Infos */}
        <div className="tpl-row-body">
          <div className="tpl-row-top">
            <h3 className="tpl-row-name">{t.name}</h3>
            {t.description && (
              <p className="tpl-row-desc">{t.description}</p>
            )}
          </div>

          <div className="tpl-row-details">
            {t.region && (
              <span className="tpl-row-tag"><MapPin size={12} /> {t.region}</span>
            )}
            {t.format && (
              <span className="tpl-row-tag"><Swords size={12} /> {formatLabel(t.format)}</span>
            )}
            {t.startDate && (
              <span className="tpl-row-tag"><Clock size={12} /> {formatDate(t.startDate)}</span>
            )}
          </div>

          {/* Barre de remplissage */}
          <div className="tpl-row-fill">
            <div className="tpl-row-fill-bar">
              <div
                className={`tpl-row-fill-progress${fillPercent >= 100 ? ' tpl-row-fill--full' : ''}`}
                style={{ width: `${Math.min(fillPercent, 100)}%` }}
              />
            </div>
            <span className="tpl-row-fill-text">
              <Users size={12} /> {teamsCount}/{maxTeams}
              {isOpen && spotsLeft > 0 && (
                <span className="tpl-row-spots"> — {spotsLeft} place{spotsLeft > 1 ? 's' : ''}</span>
              )}
            </span>
          </div>
        </div>

        {/* CTA droite */}
        <div className="tpl-row-cta">
          <span className={`tpl-row-btn${isOpen ? ' tpl-row-btn--open' : ''}`}>
            {isOpen ? 'Rejoindre' : 'Voir'}
            <ChevronRight size={15} />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}
